// src/components/ForecastTabs.js

import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Stack,
  Tabs,
  Tab,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";

import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";

import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";

import { apiFetch } from "../api/http"; // ✅ centralized, token-aware HTTP helper

// -------------------------------
// Helpers
// -------------------------------
const currencyFormatter = (value) =>
  typeof value === "number"
    ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : value;

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Keeps “Unknown / not set” out of the UI unless absolutely necessary
const normalizeSource = (deal) => {
  const raw =
    deal.source?.toString().trim() ||
    deal.lead_source?.toString().trim() ||
    "";
  return raw || "Imported";
};

const normalizeType = (deal) => {
  const raw = deal.type?.toString().trim() || "";
  return raw || "—";
};

const dateInLastNDays = (dateString, n) => {
  if (!dateString) return false;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return false;
  const now = Date.now();
  const diffDays = (now - d.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= n;
};

const getClosingDate = (d) =>
  d.close_date || d.expected_close_date || d.updated_at || d.created_at || null;

// Map stage into realtor-friendly bucket
const stageBucket = (stageRaw) => {
  if (!stageRaw) return "Other";
  const s = stageRaw.toLowerCase();

  // common cases
  if (s.includes("under") && s.includes("contract")) return "Under Contract";
  if (s.includes("contract")) return "Under Contract";

  if (s.includes("closed") || s.includes("won") || s === "past client") return "Closed";

  // “buyer” buckets
  if (s.includes("buyer")) return "Active Buyer";

  // “listing/seller” buckets
  if (s.includes("listing") || s.includes("seller")) return "Active Listing";

  // leads
  if (s.includes("lead")) return "Lead";

  // fallback
  return "Other";
};

// Commission calc:
// - Uses estimated_commission from API if available (backend-computed).
// - Falls back to dealAmount * commissionRate (default 3%) for legacy records.
const computeDealAndCommission = (deal) => {
  const dealAmount = safeNumber(deal.value);

  let commissionRate = 0.03; // default 3%
  if (deal.commission_rate != null) {
    const r = Number(deal.commission_rate);
    if (Number.isFinite(r) && r > 0) {
      commissionRate = r;
    }
  }

  const commission = deal.estimated_commission != null
    ? safeNumber(deal.estimated_commission)
    : dealAmount * commissionRate;

  return { dealAmount, commission, commissionRate };
};

// Months label like "Oct 2025"
const monthLabel = (yyyyMm) => {
  const [y, m] = yyyyMm.split("-");
  const dateObj = new Date(Number(y), Number(m) - 1, 1);
  return dateObj.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

// -------------------------------
// Component
// -------------------------------
const ForecastTabs = () => {
  const theme = useTheme();

  // hook into global AI status from DashboardLayout
  const outletCtx = useOutletContext() || {};
  const { onAiStatusChange, aiRefreshToken } = outletCtx;

  const [tab, setTab] = useState(0);
  const [days, setDays] = useState(90);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);

  // -------------------------------
  // Fetch deals (JWT-aware)
  // -------------------------------
  const loadDeals = useCallback(async () => {
    try {
      setLoading(true);
      onAiStatusChange?.({ loading: true, lastUpdated: null });

      const res = await apiFetch("/api/deals/");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDeals(Array.isArray(data) ? data : []);

      onAiStatusChange?.({ loading: false, lastUpdated: new Date() });
    } catch (err) {
      console.error("Error loading deals for revenue view:", err);
      setDeals([]);
      onAiStatusChange?.({ loading: false, lastUpdated: null });
    } finally {
      setLoading(false);
    }
  }, [onAiStatusChange]);

  useEffect(() => {
    loadDeals();
  }, [loadDeals, aiRefreshToken]);

  // -------------------------------
  // Derived data
  // -------------------------------
  const enrichedDeals = useMemo(
    () =>
      deals.map((d) => {
        const bucket = stageBucket(d.stage);
        const closingDate = getClosingDate(d);
        const { dealAmount, commission, commissionRate } = computeDealAndCommission(d);

        return {
          ...d,
          _stageBucket: bucket,
          _closingDate: closingDate,
          _dealAmount: dealAmount,
          _commission: commission,
          _commissionRate: commissionRate,
          _sourceUi: normalizeSource(d),
          _typeUi: normalizeType(d),
        };
      }),
    [deals]
  );

  const closedDeals = useMemo(
    () => enrichedDeals.filter((d) => d._stageBucket === "Closed"),
    [enrichedDeals]
  );

  // Pipeline = where money is “sitting”
  const pipelineDeals = useMemo(
    () =>
      enrichedDeals.filter((d) =>
        ["Lead", "Active Buyer", "Active Listing", "Under Contract"].includes(d._stageBucket)
      ),
    [enrichedDeals]
  );

  // -------------------------------
  // KPI metrics
  // -------------------------------
  const kpis = useMemo(() => {
    // stage probability weights for projected pipeline commission
    const stageWeights = {
      Lead: 0.15,
      "Active Buyer": 0.4,
      "Active Listing": 0.45,
      "Under Contract": 0.8,
    };

    const projectedPipelineCommission = pipelineDeals.reduce((sum, d) => {
      const w = stageWeights[d._stageBucket] ?? 0.25;
      return sum + d._commission * w;
    }, 0);

    const closedWindow = closedDeals.filter((d) => dateInLastNDays(d._closingDate, days));
    const closedCommissionWindow = closedWindow.reduce((sum, d) => sum + d._commission, 0);

    const totalClosedAmount = closedDeals.reduce((sum, d) => sum + d._dealAmount, 0);
    const avgClosedDealSize = closedDeals.length > 0 ? totalClosedAmount / closedDeals.length : 0;

    // avg closed commission per deal (helps “how many deals to hit goal” thinking)
    const totalClosedCommission = closedDeals.reduce((sum, d) => sum + d._commission, 0);
    const avgClosedCommission = closedDeals.length > 0 ? totalClosedCommission / closedDeals.length : 0;

    return {
      projectedPipelineCommission,
      closedCommissionWindow,
      avgClosedDealSize,
      avgClosedCommission,
    };
  }, [pipelineDeals, closedDeals, days]);

  // -------------------------------
  // Tab A: "Where your money is sitting" (Pipeline by stage)
  // -------------------------------
  const pipelineByStageData = useMemo(() => {
    if (!pipelineDeals.length) return [];

    const order = ["Lead", "Active Buyer", "Active Listing", "Under Contract"];
    const map = new Map();

    pipelineDeals.forEach((d) => {
      const key = d._stageBucket;
      const entry =
        map.get(key) || {
          stage: key,
          volume: 0,
          commissionPotential: 0,
          count: 0,
        };
      entry.volume += d._dealAmount;
      entry.commissionPotential += d._commission;
      entry.count += 1;
      map.set(key, entry);
    });

    // Ensure all stages exist (so chart feels stable)
    order.forEach((s) => {
      if (!map.has(s)) map.set(s, { stage: s, volume: 0, commissionPotential: 0, count: 0 });
    });

    return order.map((s) => map.get(s));
  }, [pipelineDeals]);

  // -------------------------------
  // Tab B: "What’s actually closing" (Closed commission by month)
  // -------------------------------
  const closedByMonthData = useMemo(() => {
    if (!closedDeals.length) return [];

    const byMonth = new Map();

    closedDeals.forEach((d) => {
      if (!d._closingDate) return;
      const dt = new Date(d._closingDate);
      if (Number.isNaN(dt.getTime())) return;

      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const entry =
        byMonth.get(key) || {
          month: key,
          monthLabel: monthLabel(key),
          closedCommission: 0,
          closedVolume: 0,
          count: 0,
        };

      entry.closedCommission += d._commission;
      entry.closedVolume += d._dealAmount;
      entry.count += 1;
      byMonth.set(key, entry);
    });

    return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [closedDeals]);

  // -------------------------------
  // Tab C: “Where it’s coming from” (Closed commission by source)
  // -------------------------------
  const closedBySourceData = useMemo(() => {
    if (!closedDeals.length) return [];

    const bySource = new Map();
    closedDeals.forEach((d) => {
      const src = d._sourceUi;
      const entry = bySource.get(src) || { source: src, closedCommission: 0, count: 0 };
      entry.closedCommission += d._commission;
      entry.count += 1;
      bySource.set(src, entry);
    });

    return Array.from(bySource.values()).sort((a, b) => b.closedCommission - a.closedCommission);
  }, [closedDeals]);

  // -------------------------------
  // Priority Deals table (pipeline + closed)
  // -------------------------------
  const priorityDeals = useMemo(() => {
    const list = enrichedDeals
      .filter((d) =>
        ["Under Contract", "Active Buyer", "Active Listing", "Lead", "Closed"].includes(d._stageBucket)
      )
      .map((d) => {
        // simple “action” heuristic for demo value
        const b = d._stageBucket;
        let action = "Follow up";
        if (b === "Under Contract") action = "Push to closing";
        if (b === "Active Listing") action = "Seller touch / pricing check";
        if (b === "Active Buyer") action = "Send matches / schedule showings";
        if (b === "Lead") action = "First contact today";
        if (b === "Closed") action = "Ask for review + referrals";

        return {
          ...d,
          _action: action,
        };
      });

    // Sort: Under Contract first, then Active, then Lead, then Closed
    const rank = (b) => {
      if (b === "Under Contract") return 0;
      if (b === "Active Listing") return 1;
      if (b === "Active Buyer") return 2;
      if (b === "Lead") return 3;
      if (b === "Closed") return 4;
      return 9;
    };

    return list
      .sort((a, b) => {
        const ra = rank(a._stageBucket);
        const rb = rank(b._stageBucket);
        if (ra !== rb) return ra - rb;

        // then sort by commission desc (money-first)
        return (b._commission || 0) - (a._commission || 0);
      })
      .slice(0, 12);
  }, [enrichedDeals]);

  const noClosedData = !loading && closedDeals.length === 0;
  const noPipelineData = !loading && pipelineDeals.length === 0;

  // -------------------------------
  // UI
  // -------------------------------
  return (
    <Paper
      sx={{
        p: 3,
        mt: 4,
        borderRadius: 2,
      }}
    >
      {/* HEADER */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AttachMoneyRoundedIcon sx={{ color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Commissions & Revenue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cash clarity in one screen: what's coming, what's earned, and what to do next.
            </Typography>
          </Box>
        </Stack>

        <TextField
          select
          size="small"
          label="Closed window"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          sx={{ width: 180 }}
        >
          {[30, 60, 90, 180, 365].map((d) => (
            <MenuItem key={d} value={d}>
              Last {d} days
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* KPI CARDS (reworded + agent-native) */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Pipeline Commission
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {currencyFormatter(kpis.projectedPipelineCommission)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Weighted by stage so you’re not counting money twice.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              background: alpha(theme.palette.success.main, 0.06),
              border: `1px solid ${alpha(theme.palette.success.main, 0.14)}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Closed Commission ({days} days)
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {currencyFormatter(kpis.closedCommissionWindow)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Only deals marked Closed in this window.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              background: alpha(theme.palette.info.main, 0.06),
              border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Avg Commission per Closed Deal
            </Typography>
            <Typography variant="h4" fontWeight={800}>
              {currencyFormatter(kpis.avgClosedCommission)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Use this to reverse-engineer monthly goals.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Tabs: reordered to match agent thinking */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Where Your Money Is Sitting" />
        <Tab label="What’s Closing" />
        <Tab label="Where Closings Come From" />
      </Tabs>

      {/* TAB 0: WHERE YOUR MONEY IS SITTING */}
      {tab === 0 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Pipeline by Stage
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This shows where your volume is stacked — and what it’s worth in commission.
            </Typography>
          </Box>

          <Box sx={{ height: 320 }}>
            {noPipelineData ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No active pipeline yet. Add deals or set stages to see pipeline value.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineByStageData} margin={{ left: 16, right: 16, top: 16, bottom: 16 }}>
                  <CartesianGrid stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <RechartsTooltip
                    formatter={(value, name) => [
                      currencyFormatter(value),
                      name === "volume" ? "Total deal volume" : "Commission potential",
                    ]}
                    labelFormatter={(label) => `Stage: ${label}`}
                    contentStyle={{
                      borderRadius: 10,
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  />
                  <Legend />
                  <Bar dataKey="volume" name="Total deal volume" radius={[8, 8, 0, 0]} fill={theme.palette.primary.main} />
                  <Bar
                    dataKey="commissionPotential"
                    name="Commission potential"
                    radius={[8, 8, 0, 0]}
                    fill={theme.palette.success.main}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Priority deals table (dynamic, realtor-usable) */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Deals That Move Your Income
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The highest-impact deals to touch first — sorted by stage and commission.
            </Typography>
          </Box>

          {priorityDeals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No deals available.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 320, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px" }}>Deal</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Stage</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Action</th>
                    <th style={{ textAlign: "right", padding: "8px" }}>Deal value</th>
                    <th style={{ textAlign: "right", padding: "8px" }}>Est. commission</th>
                  </tr>
                </thead>
                <tbody>
                  {priorityDeals.map((d) => (
                    <tr
                      key={d.id}
                      style={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}
                    >
                      <td style={{ padding: "8px" }}>{d.title || `Deal #${d.id}`}</td>
                      <td style={{ padding: "8px" }}>
                        <Chip
                          size="small"
                          label={d._stageBucket}
                          sx={{
                            fontWeight: 600,
                            background:
                              d._stageBucket === "Under Contract"
                                ? alpha(theme.palette.warning.main, 0.15)
                                : d._stageBucket === "Closed"
                                ? alpha(theme.palette.success.main, 0.15)
                                : alpha(theme.palette.primary.main, 0.12),
                          }}
                        />
                      </td>
                      <td style={{ padding: "8px", fontWeight: 600 }}>{d._action}</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>{currencyFormatter(d._dealAmount || 0)}</td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          fontWeight: 700,
                          color:
                            d._stageBucket === "Closed"
                              ? theme.palette.success.main
                              : theme.palette.text.primary,
                        }}
                      >
                        {currencyFormatter(d._commission || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      )}

      {/* TAB 1: WHAT’S CLOSING */}
      {tab === 1 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Closed Commission by Month
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Momentum view — quick proof of production.
            </Typography>
          </Box>

          <Box sx={{ height: 320 }}>
            {noClosedData ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No closed deals yet. Mark deals as Closed to see monthly commission.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={closedByMonthData} margin={{ left: 16, right: 16, top: 16, bottom: 16 }}>
                  <CartesianGrid stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <RechartsTooltip
                    formatter={(value, name) => [
                      currencyFormatter(value),
                      name === "closedCommission" ? "Closed commission" : "Closed volume",
                    ]}
                    contentStyle={{
                      borderRadius: 10,
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="closedCommission"
                    name="Closed commission"
                    radius={[8, 8, 0, 0]}
                    fill={theme.palette.success.main}
                  />
                  <Bar
                    dataKey="closedVolume"
                    name="Closed volume"
                    radius={[8, 8, 0, 0]}
                    fill={alpha(theme.palette.primary.main, 0.65)}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>
      )}

      {/* TAB 2: WHERE CLOSINGS COME FROM */}
      {tab === 2 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Closed Commission by Lead Source
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Helps you double down on what’s working.
            </Typography>
          </Box>

          <Box sx={{ height: 320 }}>
            {noClosedData ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No closed deals yet. Once deals are Closed, you’ll see source performance here.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={closedBySourceData}
                  layout="vertical"
                  margin={{ left: 120, right: 16, top: 16, bottom: 16 }}
                >
                  <CartesianGrid stroke={alpha(theme.palette.divider, 0.5)} horizontal />
                  <XAxis type="number" tickFormatter={currencyFormatter} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis type="category" dataKey="source" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} width={140} />
                  <RechartsTooltip
                    formatter={(value) => [currencyFormatter(value), "Closed commission"]}
                    labelFormatter={(label) => `Source: ${label}`}
                    contentStyle={{
                      borderRadius: 10,
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  />
                  <Bar
                    dataKey="closedCommission"
                    name="Closed commission"
                    radius={[8, 8, 8, 8]}
                    fill={theme.palette.primary.main}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Deals table stays useful in any tab: show closed + pipeline */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Deals (pipeline & closed)
          </Typography>

          {enrichedDeals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No deals in the system yet.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 280, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px" }}>Deal</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Type</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Stage</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Source</th>
                    <th style={{ textAlign: "left", padding: "8px" }}>Close date</th>
                    <th style={{ textAlign: "right", padding: "8px" }}>Deal size</th>
                    <th style={{ textAlign: "right", padding: "8px" }}>Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {enrichedDeals
                    .filter((d) =>
                      ["Closed", "Under Contract", "Active Buyer", "Active Listing", "Lead"].includes(d._stageBucket)
                    )
                    .sort((a, b) => {
                      const rank = (x) => {
                        if (x._stageBucket === "Under Contract") return 0;
                        if (x._stageBucket === "Active Listing") return 1;
                        if (x._stageBucket === "Active Buyer") return 2;
                        if (x._stageBucket === "Lead") return 3;
                        if (x._stageBucket === "Closed") return 4;
                        return 9;
                      };
                      const ra = rank(a);
                      const rb = rank(b);
                      if (ra !== rb) return ra - rb;
                      return (b._commission || 0) - (a._commission || 0);
                    })
                    .slice(0, 30)
                    .map((d) => (
                      <tr key={d.id} style={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
                        <td style={{ padding: "8px" }}>{d.title || `Deal #${d.id}`}</td>
                        <td style={{ padding: "8px" }}>{d._typeUi}</td>
                        <td style={{ padding: "8px" }}>{d._stageBucket}</td>
                        <td style={{ padding: "8px" }}>{d._sourceUi}</td>
                        <td style={{ padding: "8px" }}>
                          {d._stageBucket === "Closed" ? d._closingDate || "—" : "—"}
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {currencyFormatter(d._dealAmount || 0)}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "right",
                            fontWeight: d._stageBucket === "Closed" ? 800 : 600,
                            color: d._stageBucket === "Closed" ? theme.palette.success.main : theme.palette.text.primary,
                          }}
                        >
                          {currencyFormatter(d._commission || 0)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ForecastTabs;

